import { useState, useEffect } from 'react';
import { acceptStoreInvite } from './hooks/useStoreApi';
import { navigate } from './App';
import { CheckCircle2, XCircle, Loader2, Store, AlertCircle } from 'lucide-react';

export const StoreInviteAccept = ({ chatId }) => {
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [storeInfo, setStoreInfo] = useState(null);

  useEffect(() => {
    const acceptInvitation = async () => {
      try {
        // Extract token from URL
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('token');

        if (!token) {
          setError('קישור הזמנה לא תקין - חסר טוקן');
          setLoading(false);
          return;
        }

        // Check if user is logged in
        if (!chatId) {
          setError('יש להתחבר כדי לקבל הזמנה');
          setLoading(false);
          // Save the token for after login
          sessionStorage.setItem('pendingInviteToken', token);
          sessionStorage.setItem('redirectAfterLogin', window.location.search);
          return;
        }

        // Accept the invitation
        const response = await acceptStoreInvite(token, chatId);

        setSuccess(true);
        setStoreInfo({
          name: response.store_name,
          slug: response.store_slug,
          storeId: response.store_id
        });

        // Redirect to store admin after 2 seconds
        setTimeout(() => {
          navigate('store-admin', `slug=${response.store_slug}`);
        }, 2000);

      } catch (err) {
        console.error('Error accepting invitation:', err);
        setError(err.message || 'שגיאה בקבלת ההזמנה');
      } finally {
        setLoading(false);
      }
    };

    acceptInvitation();
  }, [chatId]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-mekomit-50 via-white to-mekomit-100 p-4" dir="rtl">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-elevated p-8 border border-mekomit-100">
        {/* Loading State */}
        {loading && (
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <Loader2 className="w-16 h-16 text-mekomit-600 animate-spin" />
            </div>
            <h2 className="text-2xl font-bold text-mekomit-900">מעבד הזמנה...</h2>
            <p className="text-mekomit-700">אנא המתן, מאמתים את קישור ההזמנה</p>
          </div>
        )}

        {/* Success State */}
        {!loading && success && storeInfo && (
          <div className="text-center space-y-6 animate-fade-in">
            <div className="flex justify-center">
              <div className="w-20 h-20 bg-success-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-12 h-12 text-success-600" />
              </div>
            </div>
            <div className="space-y-2">
              <h2 className="text-2xl font-bold text-success-900">הצלחה!</h2>
              <p className="text-lg text-ink">
                נוספת בהצלחה כמנהל משנה לחנות
              </p>
              <div className="flex items-center justify-center gap-2 text-mekomit-700 font-semibold">
                <Store className="w-5 h-5" />
                <span>{storeInfo.name}</span>
              </div>
            </div>
            <div className="p-4 bg-info-50 border border-info-200 rounded-lg">
              <p className="text-sm text-info-800">
                מעביר אותך לניהול החנות...
              </p>
            </div>
          </div>
        )}

        {/* Error State */}
        {!loading && error && (
          <div className="text-center space-y-6 animate-fade-in">
            <div className="flex justify-center">
              <div className="w-20 h-20 bg-danger-100 rounded-full flex items-center justify-center">
                {error.includes('חסר') || error.includes('לא תקין') ? (
                  <AlertCircle className="w-12 h-12 text-danger-600" />
                ) : (
                  <XCircle className="w-12 h-12 text-danger-600" />
                )}
              </div>
            </div>
            <div className="space-y-2">
              <h2 className="text-2xl font-bold text-danger-900">שגיאה</h2>
              <p className="text-lg text-ink">{error}</p>
            </div>

            {/* If user needs to log in */}
            {!chatId && (
              <div className="space-y-3">
                <div className="p-4 bg-warning-50 border border-warning-200 rounded-lg">
                  <p className="text-sm text-warning-800">
                    קישור ההזמנה נשמר. אנא התחבר כדי להמשיך.
                  </p>
                </div>
                <button
                  onClick={() => {
                    navigate('zones');
                    window.location.reload();
                  }}
                  className="w-full px-4 py-3 bg-gradient-to-r from-mekomit-500 to-mekomit-400 text-white rounded-lg hover:from-mekomit-600 hover:to-mekomit-500 transition-all shadow-md hover:shadow-lg font-semibold"
                >
                  התחבר כעת
                </button>
              </div>
            )}

            {/* If error is expired/invalid token */}
            {chatId && (
              <div className="space-y-3">
                <div className="p-4 bg-info-50 border border-info-200 rounded-lg text-right">
                  <p className="text-sm text-info-800 font-medium mb-2">סיבות אפשריות:</p>
                  <ul className="text-sm text-info-700 space-y-1 list-disc list-inside">
                    <li>הקישור פג תוקף (תוקף של 5 דקות)</li>
                    <li>הקישור כבר נוצל</li>
                    <li>אתה כבר מנהל של החנות</li>
                  </ul>
                </div>
                <button
                  onClick={() => navigate('zones')}
                  className="w-full px-4 py-3 bg-surface-sunken text-ink-strong rounded-lg hover:bg-line transition-all font-semibold"
                >
                  חזור לאזורים
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
